import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button, Input, TextArea } from "../common/Primitives";
import { Plus, Trash2, ShieldCheck, Save, X } from "lucide-react";
import { formatDate } from "../../lib/datetime";

const INSTRUMENTS = ["Equity", "CCPS", "SAFE", "Convertible Debt", "Other"];
const ROUND_NAMES = ["Pre-Seed", "Seed", "Bridge", "Series A", "Series B",
                      "Series C", "Series D", "Debt", "Other"];

/**
 * StartupFundingRoundsSection — structured "Prior Funding Rounds" editor.
 * Sits beneath the free-text `previous_rounds` field on FundingProfile.
 */
export default function StartupFundingRoundsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);       // full round object
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/startup/funding-rounds");
      setItems(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const emptyRound = () => ({
    round_name: "Seed",
    amount_raised_inr: "",
    valuation_inr: "",
    round_date: "",
    lead_investor: "",
    co_investors: "",
    instrument: "Equity",
    stake_diluted_pct: "",
    notes: "",
  });

  const save = async (draft) => {
    setBusy(true);
    try {
      const body = {
        round_name: draft.round_name,
        amount_raised_inr: draft.amount_raised_inr ? Number(draft.amount_raised_inr) : null,
        valuation_inr: draft.valuation_inr ? Number(draft.valuation_inr) : null,
        round_date: draft.round_date || null,
        lead_investor: draft.lead_investor || null,
        co_investors: draft.co_investors
          ? draft.co_investors.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        instrument: draft.instrument || null,
        stake_diluted_pct: draft.stake_diluted_pct ? Number(draft.stake_diluted_pct) : null,
        notes: draft.notes || null,
      };
      if (draft.round_id) {
        await api.patch(`/startup/funding-rounds/${draft.round_id}`, body);
        toast.success("Round updated");
      } else {
        await api.post("/startup/funding-rounds", body);
        toast.success("Round added");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (roundId) => {
    if (!window.confirm("Delete this funding round?")) return;
    setBusy(true);
    try {
      await api.delete(`/startup/funding-rounds/${roundId}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const totalRaised = items.reduce((s, r) => s + (r.amount_raised_inr || 0), 0);

  return (
    <section className="border border-white/10 bg-white/70 backdrop-blur p-6 rounded-lg" data-testid="funding-rounds-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Prior funding rounds</div>
          <div className="text-xs text-ink-muted mt-1">
            Structured history — investors trust verified rounds more than free-text summaries.
            <span className="ml-2 font-mono">
              Total: ₹{totalRaised.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
        <Button
          variant="gold"
          onClick={() => setEditing(emptyRound())}
          type="button"
          data-testid="round-add"
        >
          <Plus size={14} className="inline mr-1" /> Add round
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-ink-muted italic">Loading rounds…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-ink-muted italic" data-testid="rounds-empty">
          No prior rounds recorded yet. Bootstrap capital or first cheque? Add it above.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.round_id} className="border border-navy/10 rounded-md p-3 bg-white flex items-start justify-between gap-3" data-testid={`round-${r.round_id}`}>
              <div className="min-w-0 flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div>
                  <div className="text-[10px] font-mono uppercase text-ink-muted">Round</div>
                  <div className="font-heading text-ink">{r.round_name}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase text-ink-muted">Raised</div>
                  <div className="font-heading text-ink">₹{(r.amount_raised_inr || 0).toLocaleString("en-IN")}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase text-ink-muted">Valuation</div>
                  <div className="font-heading text-ink">
                    {r.valuation_inr ? `₹${r.valuation_inr.toLocaleString("en-IN")}` : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase text-ink-muted">Date</div>
                  <div className="font-heading text-ink">{r.round_date ? formatDate(r.round_date) : "—"}</div>
                </div>
                {(r.lead_investor || (r.co_investors || []).length > 0) && (
                  <div className="col-span-2 sm:col-span-4">
                    <div className="text-[10px] font-mono uppercase text-ink-muted">Investors</div>
                    <div className="text-sm text-ink-soft">
                      {[r.lead_investor, ...(r.co_investors || [])].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                {r.verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full" data-testid={`round-verified-${r.round_id}`}>
                    <ShieldCheck size={10} /> Verified
                  </span>
                )}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing({
                      ...r,
                      co_investors: (r.co_investors || []).join(", "),
                    })}
                    className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading hover:border-emerald-500"
                    data-testid={`round-edit-${r.round_id}`}
                  >Edit</button>
                  <button
                    type="button"
                    onClick={() => remove(r.round_id)}
                    className="px-2 py-1 rounded border border-rose-300 text-[11px] font-heading text-rose-700 hover:bg-rose-50"
                    data-testid={`round-delete-${r.round_id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <RoundEditor
          draft={editing}
          busy={busy}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={() => save(editing)}
        />
      )}
    </section>
  );
}

function RoundEditor({ draft, onChange, onSave, onCancel, busy }) {
  const patch = (k) => (e) => onChange({ ...draft, [k]: e.target.value });
  return (
    <div className="mt-4 border-t border-navy/10 pt-4" data-testid="round-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{draft.round_id ? "Edit round" : "New round"}</div>
        <button type="button" onClick={onCancel} className="text-ink-muted hover:text-ink" data-testid="round-cancel">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Round name</label>
          <select
            value={draft.round_name}
            onChange={patch("round_name")}
            className="mt-1 w-full px-3 py-2 rounded border border-navy/15 text-sm bg-white"
            data-testid="round-name"
          >
            {ROUND_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Instrument</label>
          <select
            value={draft.instrument || "Equity"}
            onChange={patch("instrument")}
            className="mt-1 w-full px-3 py-2 rounded border border-navy/15 text-sm bg-white"
            data-testid="round-instrument"
          >
            {INSTRUMENTS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <Input label="Amount raised (₹)" type="number" value={draft.amount_raised_inr}
          onChange={patch("amount_raised_inr")} testId="round-amount" />
        <Input label="Post-money valuation (₹)" type="number" value={draft.valuation_inr}
          onChange={patch("valuation_inr")} testId="round-valuation" />
        <Input label="Round date" type="date" value={draft.round_date}
          onChange={patch("round_date")} testId="round-date" />
        <Input label="Stake diluted (%)" type="number" step="0.01" value={draft.stake_diluted_pct}
          onChange={patch("stake_diluted_pct")} testId="round-stake" />
        <Input label="Lead investor" value={draft.lead_investor}
          onChange={patch("lead_investor")} testId="round-lead" />
        <Input label="Co-investors (comma separated)" value={draft.co_investors}
          onChange={patch("co_investors")} testId="round-coinv" />
      </div>
      <div className="mt-3">
        <TextArea label="Notes" value={draft.notes} onChange={patch("notes")} testId="round-notes" rows={2} />
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="outline" type="button" onClick={onCancel} data-testid="round-cancel-btn">Cancel</Button>
        <Button variant="gold" type="button" onClick={onSave} disabled={busy} data-testid="round-save">
          <Save size={14} className="inline mr-1" /> {busy ? "Saving…" : "Save round"}
        </Button>
      </div>
    </div>
  );
}
